<template>
    <div class="pd-overview" v-if="pd">
        <div class="columns is-variable is-5 pd-columns">
        <div class="column main-col">
        <!-- What the settings are set at; editing lives on the Settings view -->
        <section-header title="Your Details" icon="fa-briefcase" :to="settingsRoute" link-text="Edit settings"/>
        <pd-details-summary class="mb-6"/>

        <!-- Where you stand: the headline numbers, then renewal + salary side by side -->
        <section-header title="Where You Stand" icon="fa-location-crosshairs"/>
        <ui-card class="mb-6">
            <div class="stat-grid">
                <router-link class="stat" :to="renewalRoute">
                    <div class="stat-value">{{ pd.hours.earned_total | hours }}</div>
                    <div class="stat-label">{{ unitTitle }} earned with us</div>
                </router-link>
                <a class="stat" href="#in-progress" @click.prevent="scrollTo('in-progress')">
                    <div class="stat-value">{{ pd.hours.in_progress.potential_hours | hours }}</div>
                    <div class="stat-label">in progress across {{ pd.hours.in_progress.count }} {{ pd.hours.in_progress.count === 1 ? 'course' : 'courses' }}</div>
                </a>
                <router-link class="stat" :to="renewalRoute">
                    <template v-if="pd.profile.declared">
                        <div class="stat-value">{{ renewalCounted | hours }}<span class="stat-of"> / {{ pd.profile.target_hours }}</span></div>
                        <div class="stat-label">toward your {{ pd.profile.renewal_on | dateOnly }} renewal</div>
                    </template>
                    <template v-else>
                        <div class="stat-value stat-muted">&mdash;</div>
                        <div class="stat-label">renewal date not declared</div>
                    </template>
                </router-link>
                <router-link class="stat" :to="salaryRoute">
                    <template v-if="firstTarget">
                        <div class="stat-value">{{ firstTarget.requiredHours }}<span class="stat-of"> hrs</span></div>
                        <div class="stat-label">to {{ firstTarget.tier }} &middot; <span class="has-text-success has-text-weight-bold">+{{ firstTarget.diff | money }}/yr</span></div>
                    </template>
                    <template v-else-if="position">
                        <div class="stat-value"><i class="fas fa-trophy has-text-warning"></i></div>
                        <div class="stat-label">top of your {{ salary.highest_degree }} track</div>
                    </template>
                    <template v-else>
                        <div class="stat-value stat-muted">&mdash;</div>
                        <div class="stat-label">next salary column</div>
                    </template>
                </router-link>
            </div>

            <div class="columns is-variable is-4 mt-4 stand-boxes">
                <!-- Salary schedule -->
                <div class="column">
                    <div class="info-box is-tall">
                        <div class="box-main">
                            <div class="box-icon"><i class="fad fa-chart-line-up fa-xl has-text-primary"></i></div>
                            <div class="box-body">
                                <div class="box-title">Salary Schedule</div>
                                <div class="box-text" v-if="!salary.ready">Add your district, degree and current clock-hour column in Settings and we'll show how close you are to your next salary column, and what it's worth.</div>
                                <div class="box-text" v-else-if="scaleLoading"><i class="fas fa-spinner fa-spin mr-2"></i>Checking {{ salary.district_name }}'s salary schedule…</div>
                                <div class="box-text" v-else-if="!scale">We don't have {{ salary.district_name || 'your district' }}'s salary schedule on file yet. Know where to find it? You can share it with us.</div>
                                <div class="box-text" v-else-if="!position">We have {{ salary.district_name }}'s schedule. Add your degree, current column and years teaching to see your placement.</div>
                                <template v-else>
                                    <div class="placement mt-1">
                                        <span class="lane-chip">{{ position.current.tier }}</span>
                                        <span class="placement-salary">{{ position.currentSalary | money }}<span class="per"> / yr</span></span>
                                    </div>
                                    <div class="box-text mt-2" v-if="firstTarget">
                                        You're about <strong>{{ firstTarget.requiredHours }} clock hours</strong> from <strong>{{ firstTarget.tier }}</strong> &mdash; worth <strong class="has-text-success">+{{ firstTarget.diff | money }}</strong> a year<template v-if="firstTargetFiveYear > 0">, about <strong>{{ firstTargetFiveYear | money }}</strong> over the next five years</template>.
                                    </div>
                                    <div class="box-text mt-2" v-else>
                                        <i class="fas fa-trophy has-text-warning mr-1"></i>You're at the top of {{ salary.district_name }}'s {{ salary.highest_degree }} track &mdash; every clock hour still counts toward your renewal.
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="box-actions">
                            <div class="box-buttons">
                                <b-button type="is-primary" size="is-small" :outlined="!firstTarget" :disabled="!salary.ready" tag="router-link" :to="salaryRoute">
                                    <i class="fas fa-chart-line-up mr-2"></i>{{ firstTarget ? "See What It's Worth" : 'Your Salary Schedule' }}
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Renewal -->
                <div class="column">
                    <div class="info-box is-tall">
                        <div class="box-main">
                            <div class="box-icon"><i class="fad fa-calendar-check fa-xl has-text-primary"></i></div>
                            <div class="box-body">
                                <div class="box-title">Certificate Renewal</div>
                                <template v-if="pd.profile.declared">
                                    <div class="renewal-bar mt-2">
                                        <div class="seg seg-earned" :style="{width: earnedPct + '%'}"></div>
                                        <div class="seg seg-progress" :style="{width: progressPct + '%'}"></div>
                                    </div>
                                    <div class="box-text mt-2">
                                        <strong>{{ renewalCounted | hours }} of {{ pd.profile.target_hours }}</strong> hours toward your renewal on <strong>{{ pd.profile.renewal_on | dateOnly }}</strong>
                                        <template v-if="pd.hours.remaining_to_target === 0"> &mdash; target reached.</template>
                                        <template v-else-if="pd.pace && pd.pace.required_per_year !== null"> &mdash; about <strong>{{ pd.pace.required_per_year | hours }} hours a year</strong> from here.</template>
                                        <template v-if="pd.hours.in_progress.potential_hours"> {{ pd.hours.in_progress.potential_hours | hours }} more are in progress.</template>
                                    </div>
                                </template>
                                <div class="box-text" v-else>Add your certificate renewal date in Settings to see the hours you've earned with us inside your renewal window.</div>
                            </div>
                        </div>
                        <div class="box-actions">
                            <div class="box-buttons">
                                <b-button type="is-primary" size="is-small" outlined tag="router-link" :to="renewalRoute">
                                    <i class="fas fa-calendar-check mr-2"></i>Renewal Progress
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </ui-card>

        <!-- In progress: the clock-hour courses being worked on -->
        <template v-if="items.length">
            <section-header id="in-progress" title="In Progress" icon="fa-circle-play" :count="items.length" :to="{path: '/enrollments'}" link-text="All course enrollments"/>
            <ui-card class="mb-6">
                <div class="progress-list">
                    <div class="progress-row" v-for="item in items" :key="item.enrollment_id">
                        <div class="row-icon"><i class="fad fa-chalkboard-teacher fa-lg has-text-primary"></i></div>
                        <div class="row-body">
                            <div class="row-title">{{ item.title }}</div>
                            <div class="row-bar">
                                <div class="bar-fill" :style="{width: pctOf(item.logged_hours, item.max_hours) + '%'}"></div>
                            </div>
                            <div class="row-meta">
                                <strong>{{ item.logged_hours | hours }} of {{ item.max_hours | hours }}</strong> hours logged
                                <template v-if="item.days_to_expiry !== null && item.days_to_expiry <= 90"> &middot; <span class="has-text-danger has-text-weight-bold"><i class="fas fa-clock mr-1"></i>Expires in {{ item.days_to_expiry }} {{ item.days_to_expiry === 1 ? 'day' : 'days' }}</span></template>
                                <template v-else-if="item.expires_on"> &middot; Expires {{ item.expires_on | dateOnly }}</template>
                            </div>
                        </div>
                        <div class="row-actions">
                            <b-button type="is-primary" size="is-small" outlined tag="router-link" :to="'/course/' + item.enrollment_id"><i class="fas fa-arrow-right mr-2"></i>Open Course</b-button>
                        </div>
                    </div>
                </div>
                <div class="mt-4 is-size-7 has-text-grey" v-if="pd.hours.pending_review">
                    <i class="far fa-hourglass-half mr-2"></i>{{ pd.hours.pending_review }} {{ pd.hours.pending_review === 1 ? 'application is' : 'applications are' }} awaiting review &mdash; hours are added once approved.
                </div>
            </ui-card>
        </template>

        <!-- History at a glance -->
        <template v-if="pd.hours.awards_count">
            <section-header title="Clock Hours by Year" icon="fa-chart-column" :to="renewalRoute" link-text="Full history"/>
            <ui-card class="mb-6">
                <hours-by-year-chart :rows="pd.by_year"/>
                <div class="mt-2 is-size-7 has-text-grey" v-if="lastAward">
                    <i class="far fa-award mr-2"></i>Most recent: <strong>{{ lastAward.units | hours }} hours</strong> for {{ lastAward.item_title || lastAward.title }} &middot; {{ lastAward.completed_at | dateOnly }}
                </div>
            </ui-card>
        </template>

        </div>

        <!-- Earn more: the bundle and the rep hand-off (copy mirrors the
             builder's entry screen), in a side column like the dashboard's -->
        <div class="column side-col">
        <maximize-promo/>
        </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import SectionHeader from "../framework/SectionHeader";
    import UiCard from "../UiCard";
    import HoursByYearChart from "./HoursByYearChart";
    import PdDetailsSummary from "./PdDetailsSummary";
    import MaximizePromo from "./MaximizePromo";
    import salaryLane from "./salaryLane";
    import {formatHours, pdRoute} from "@/utils/pd";

    /**
     * Professional Development > Overview: the "where you stand" page —
     * headline numbers, renewal + salary placement side by side, the
     * clock-hour courses in progress, the by-year history and the ways to
     * earn more.
     */
    export default {
        name: "PdOverview",
        components: {SectionHeader, UiCard, HoursByYearChart, PdDetailsSummary, MaximizePromo},
        mixins: [salaryLane],
        filters: {hours: formatHours},
        computed: {
            ...mapGetters(['pdSummary']),
            pd() {
                return this.pdSummary;
            },
            unitTitle() {
                return ((this.pd.certification && this.pd.certification.unit_title) || 'Clock hours').toLowerCase();
            },
            renewalRoute() {
                return pdRoute('renewal');
            },
            salaryRoute() {
                return pdRoute('salary');
            },
            settingsRoute() {
                return pdRoute('settings');
            },
            renewalCounted() {
                return (parseFloat(this.pd.hours.counted) || 0) + (parseFloat(this.pd.hours.external) || 0);
            },
            earnedPct() {
                return this.pct(this.renewalCounted);
            },
            // In-progress hours fill only the room left after what's earned
            progressPct() {
                return Math.min(100 - this.earnedPct, this.pct(this.pd.hours.in_progress.potential_hours));
            },
            items() {
                return (this.pd.hours.in_progress && this.pd.hours.in_progress.items) || [];
            },
            lastAward() {
                return (this.pd.awards && this.pd.awards[0]) || null;
            }
        },
        methods: {
            pct(hours) {
                const target = this.pd.profile.target_hours || 0;
                if (!target) return 0;
                return Math.max(0, Math.min(100, (parseFloat(hours) || 0) / target * 100));
            },
            pctOf(a, b) {
                if (!b) return 0;
                return Math.max(0, Math.min(100, (parseFloat(a) || 0) / b * 100));
            },
            scrollTo(id) {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
            }
        }
    };
</script>

<style lang="scss" scoped>
    .intro {
        max-width: 75ch;
    }

    // Two columns only on wide windows, same rule as the dashboard: with the
    // side panel open the 1/3 column is too cramped below ~1600px, so the
    // sections stack until then.
    .pd-columns {
        display: block;

        @media (min-width: 1600px) {
            display: flex;

            .main-col {
                flex: none;
                width: 66.6667%;
            }

            .side-col {
                flex: none;
                width: 33.3333%;
            }
        }
    }

    .stat-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
        gap: 1rem;

        .stat {
            display: block;
            padding: 0.75rem 1rem;
            border-radius: 8px;
            background: #fafbfc;
            border: 1px solid #eef1f4;
            color: inherit;
            transition: background-color 150ms, border-color 150ms;

            &:hover {
                background: #f2f7fc;
                border-color: #d6e4f2;
            }
        }

        .stat-value {
            font-size: 1.8rem;
            font-weight: 800;
            line-height: 1.1;
            color: #336699;

            .stat-of {
                font-size: 1rem;
                font-weight: 600;
                color: #7a7a7a;
            }

            &.stat-muted {
                color: #b3bcc7;
            }
        }

        .stat-label {
            font-size: 0.8rem;
            color: #7a7a7a;
            margin-top: 0.15rem;
        }
    }

    // Both boxes stretch to the row so the footers line up
    .stand-boxes {
        .column {
            display: flex;
        }

        .info-box.is-tall {
            display: flex;
            flex-direction: column;
            width: 100%;

            .box-main {
                flex: 1;
                gap: 0.9rem;
            }

            // Column sized to the smaller icon
            .box-icon {
                width: 2rem;
            }
        }
    }

    .renewal-bar {
        display: flex;
        height: 0.6rem;
        border-radius: 999px;
        background: #ececec;
        overflow: hidden;
        max-width: 420px;

        .seg-earned {
            background: #336699;
        }

        .seg-progress {
            background: #9dbbd8;
        }
    }

    .placement {
        display: flex;
        align-items: baseline;
        gap: 0.75rem;
        flex-wrap: wrap;

        .lane-chip {
            display: inline-block;
            background: #2e5f8f;
            color: #fff;
            font-size: 0.75rem;
            font-weight: bold;
            padding: 3px 10px;
            border-radius: 999px;
            white-space: nowrap;
        }

        .placement-salary {
            font-size: 1.3rem;
            font-weight: 900;
            color: #2e5f8f;

            .per {
                font-size: 0.85rem;
                font-weight: bold;
                color: #666d7a;
            }
        }
    }

    .progress-list {
        .progress-row {
            display: grid;
            grid-template-columns: 2.25rem minmax(0, 1fr) auto;
            gap: 0.9rem;
            align-items: start;
            padding: 0.85rem 0;
            border-bottom: 1px solid #e8eef4;

            &:first-child {
                padding-top: 0;
            }

            &:last-child {
                border-bottom: none;
                padding-bottom: 0;
            }

            .row-icon {
                text-align: center;
                padding-top: 0.1rem;
            }

            .row-title {
                font-weight: 600;
                color: #33475b;
            }

            .row-bar {
                height: 0.4rem;
                border-radius: 999px;
                background: #ececec;
                overflow: hidden;
                max-width: 360px;
                margin-top: 0.4rem;

                .bar-fill {
                    height: 100%;
                    background: #336699;
                }
            }

            .row-meta {
                font-size: 0.8rem;
                color: #7a8794;
                margin-top: 0.35rem;
            }

            @media (max-width: 600px) {
                grid-template-columns: 2.25rem minmax(0, 1fr);

                .row-actions {
                    grid-column: 2;
                }
            }
        }
    }
</style>
